import { ResponsiveLine } from "@nivo/line";
import { nFormatter } from "~/utils/formatNumbers";
import { BRAND_PRIMARY_RGB, BRAND_SECONDARY_RGB } from "~/brandColors";
import { InsightText } from "~/translations/dashboard/insights";
import type { Language } from "~/utils/language";

export interface GrowthGraphData {
  id: string;
  color: string;
  data: {
    x: number;
    y: number;
  }[];
}

export function generateInsightCardGrowthChart(
  dataGraphs: GrowthGraphData[],
  lang: Language = "HEB",
  currencySymbol: string = "₪"
) {
  const t = InsightText[lang];
  const isEng = lang === "ENG";
  let tickValues = reduceTickValues(dataGraphs);
  return (
    <div className="h-[300px] min-h-[300px] w-full min-w-[200px] mobile:h-[300px]">
      <ResponsiveLine
        margin={{
          top: 20,
          right: 10,
          bottom: isEng ? 50 : 40,
          left: isEng ? 50 : 40,
        }}
        animate={true}
        enableSlices={"x"}
        markers={
          findMinPoint(dataGraphs) < 0
            ? [
                {
                  axis: "y",
                  lineStyle: {
                    stroke: "#b0413e",
                    strokeWidth: 1,
                  },
                  value: 0,
                },
              ]
            : []
        }
        sliceTooltip={({ slice }) => {
          return (
            <div
              style={{
                background: "white",
                padding: "9px 12px",
                border: "1px solid #ccc",
              }}
            >
              <div>
                <strong>
                  {
                    // Check if this data point has age information
                    (slice.points[0].data as any).age
                      ? slice.points[0].data.xFormatted == "0"
                        ? t.today
                        : `${t.atAge} ${(slice.points[0].data as any).age}`
                      : slice.points[0].data.xFormatted == "0"
                      ? t.today
                      : slice.points[0].data.xFormatted == "1"
                      ? t.inOneYear
                      : t.inYears.replace(
                          "{0}",
                          String(slice.points[0].data.xFormatted)
                        )
                  }
                </strong>
              </div>
              {slice.points.map((point) => (
                <div
                  key={point.id}
                  style={{
                    color: point.serieColor,
                    padding: "3px 0",
                  }}
                >
                  <strong>{point.serieId}</strong> {point.data.yFormatted}
                </div>
              ))}
              {slice.points.length > 1 && (
                <div>
                  <strong>
                    {t.difference}{" "}
                    {`${currencySymbol}${nFormatter(
                      Math.round(
                        Number(slice.points[0].data.y) -
                          Number(slice.points[1].data.y)
                      ),
                      3
                    )}`}
                  </strong>
                </div>
              )}
            </div>
          );
        }}
        enablePoints={false}
        colors={[BRAND_PRIMARY_RGB, BRAND_SECONDARY_RGB]}
        data={dataGraphs}
        enableGridX={false}
        yScale={{
          type: "linear",
          min: dataGraphs
            ? findMinPoint(dataGraphs) - Math.abs(findMinPoint(dataGraphs) / 20)
            : "auto",
          max:
            findMaxPoint(dataGraphs) + Math.abs(findMaxPoint(dataGraphs) / 10),
        }}
        yFormat={(v) =>
          `${currencySymbol}${nFormatter(Math.round(Math.abs(Number(v))), 3)}${
            Number(v) < 0 ? "-" : ""
          }`
        }
        useMesh={true}
        enableArea={true}
        areaBaselineValue={dataGraphs[0].data[0].y}
        axisLeft={{
          tickValues: 6,
          tickSize: 0,
          tickPadding: isEng ? 5 : 38,
          tickRotation: 0,
          format: (v) =>
            `${currencySymbol}${nFormatter(Math.round(Math.abs(v)), 3)}${
              v < 0 ? "-" : ""
            }`,
        }}
        axisBottom={{
          tickValues: tickValues,
          tickSize: 2,
          tickPadding: 4,
          legend: hasAgeData(dataGraphs) ? t.age : t.years,
          legendPosition: "middle",
          legendOffset: isEng ? -10 : -10,
          format: hasAgeData(dataGraphs)
            ? (value) => formatAgeAxis(value, dataGraphs, lang)
            : undefined,
        }}
        legends={[
          {
            anchor: isEng ? "top-left" : "top-left",
            direction: "row",
            justify: isEng ? false : true,
            translateY: -20,

            itemsSpacing: isEng ? 10 : 100,
            itemDirection: isEng ? "left-to-right" : "left-to-right",

            itemWidth: isEng ? 180 : 20,
            itemHeight: 20,
            itemOpacity: 0.75,
            symbolSize: 12,
            symbolShape: "circle",
            symbolBorderColor: "rgba(0, 0, 0, .5)",
            effects: [
              {
                on: "hover",
                style: {
                  itemBackground: "rgba(0, 0, 0, .03)",
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
      />
    </div>
  );
}

export function findMinPoint(data: GrowthGraphData[]): number {
  let min = data[0].data[0].y;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y < min) {
        min = point.y;
      }
    });
  });
  return min;
}

export function findMaxPoint(data: GrowthGraphData[]): number {
  let max = data[0].data[0].y;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.y > max) {
        max = point.y;
      }
    });
  });
  return max;
}

function reduceTickValues(data: GrowthGraphData[]): number[] {
  //this function will return 6 tick values for the x axis
  let tickValues = [];
  let max = 0;
  data.forEach((graph) => {
    graph.data.forEach((point) => {
      if (point.x > max) {
        max = point.x;
      }
    });
  });
  let interval = Math.ceil(max / 8);
  for (let i = 0; i < 8; i++) {
    if (i * interval > max) {
      break;
    }
    tickValues.push(i * interval);
  }
  return tickValues;
}

function hasAgeData(data: GrowthGraphData[]): boolean {
  // Check if any data point has age information
  return data.some((graph) =>
    graph.data.some((point) => (point as any).age !== undefined)
  );
}

function formatAgeAxis(
  value: number,
  data: GrowthGraphData[],
  lang: Language = "HEB"
): string {
  // Find the data point with this x value and return its age
  if (value === 0) return InsightText[lang].todayAxis;

  const firstGraph = data[0];
  const dataPoint = firstGraph.data.find((point) => point.x === value);
  if (dataPoint && (dataPoint as any).age) {
    return `${(dataPoint as any).age}`;
  }

  // Fallback - calculate age based on first data point
  const firstDataPoint = firstGraph.data[0];
  if (firstDataPoint && (firstDataPoint as any).age) {
    const baseAge = (firstDataPoint as any).age;
    return `${baseAge + value}`;
  }

  return `${value}`;
}
